import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Field } from "@/components/common";
import { useAuth, homeFor } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1564415315949-7a0c4c73aab4?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgot, setForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={params.get("next") || homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (forgot) {
        const { data } = await api.post("/auth/forgot-password", { email });
        toast.success(data.message);
        setForgot(false);
      } else {
        const u = await login(email, password);
        nav(params.get("next") || homeFor(u), { replace: true });
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block mat-texture overflow-hidden">
        <img src={HERO} alt="Treino de Jiu-Jitsu" className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Logo />
          <div className="fade-up">
            <div className="eyebrow mb-3">Sistema de gestão</div>
            <h1 className="text-6xl font-extrabold uppercase text-white leading-[0.95]">Disciplina no tatame.<br /><span className="text-red-600">Controle</span> na gestão.</h1>
            <p className="text-zinc-400 mt-5 max-w-md">Alunos, check-in, pagamentos, graduações e contratos da academia em um único lugar.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="login-form">
          <div className="lg:hidden mb-6"><Logo dark={false} /></div>
          <div>
            <div className="eyebrow">{forgot ? "Recuperar acesso" : "Bem-vindo de volta"}</div>
            <h2 className="text-4xl font-extrabold uppercase mt-1">{forgot ? "Esqueci a senha" : "Entrar"}</h2>
          </div>
          <Field label="E-mail">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
          </Field>
          {!forgot && (
            <Field label="Senha">
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </Field>
          )}
          {error && <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-red-600 hover:bg-red-700 font-bold uppercase tracking-wider" data-testid="login-submit-btn">
            {loading ? "Aguarde…" : forgot ? "Enviar instruções" : "Entrar"}
          </Button>
          <button type="button" onClick={() => { setForgot(!forgot); setError(""); }} className="text-sm text-slate-500 hover:text-red-600" data-testid="toggle-forgot-btn">
            {forgot ? "Voltar ao login" : "Esqueci minha senha"}
          </button>
        </form>
      </div>
    </div>
  );
}
